// Precondition: None
// Postcondition: Custom element that looks like a search field but acts as a button
// Invariants: Dispatches ft-spotlight-open on click

import { icon } from '../../../shared/icons.js';

class FtSearchButton extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  // Precondition: None
  // Postcondition: Returns placeholder text from attribute or default
  private getPlaceholder(): string {
    return this.getAttribute("placeholder") || "Search";
  }

  // Precondition: None
  // Postcondition: Returns shortcut text from attribute or default
  private getShortcut(): string {
    return this.getAttribute("shortcut") || "⌘K";
  }

  // Precondition: Shadow DOM attached
  // Postcondition: Renders HTML structure into shadow DOM
  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  // Precondition: size is one of 'small', 'normal', 'large'
  // Postcondition: Returns CSS string with size-specific properties
  // Uses global height variables: --height-sm, --height-md, --height-lg
  private getSizeStyles(size: string): {
    button: string;
    icon: string;
    placeholder: string;
    shortcut: string;
  } {
    switch (size) {
      case "small":
        return {
          button: `
            height: var(--height-sm, 32px);
            padding: 0 12px;
            gap: 8px;
            min-width: 160px;
          `,
          icon: `
            width: 14px;
            height: 14px;
          `,
          placeholder: `
            font: 400 12px/1 Inter, system-ui, sans-serif;
          `,
          shortcut: `
            padding: 2px 6px;
            font: 500 9px/1 Inter, system-ui, sans-serif;
          `,
        };
      case "large":
        return {
          button: `
            height: var(--height-lg, 48px);
            padding: 0 20px;
            gap: 14px;
            min-width: 280px;
          `,
          icon: `
            width: 20px;
            height: 20px;
          `,
          placeholder: `
            font: 400 16px/1 Inter, system-ui, sans-serif;
          `,
          shortcut: `
            padding: 5px 10px;
            font: 500 12px/1 Inter, system-ui, sans-serif;
          `,
        };
      case "normal":
      default:
        return {
          button: `
            height: var(--height-md, 40px);
            padding: 0 16px;
            gap: 12px;
            min-width: 200px;
          `,
          icon: `
            width: 18px;
            height: 18px;
          `,
          placeholder: `
            font: 400 14px/1 Inter, system-ui, sans-serif;
          `,
          shortcut: `
            padding: 4px 8px;
            font: 500 11px/1 Inter, system-ui, sans-serif;
          `,
        };
    }
  }

  // Precondition: None
  // Postcondition: Returns CSS string for shadow DOM
  private getStyles(): string {
    const size = this.getAttribute("size") || "normal";
    const sizeStyles = this.getSizeStyles(size);

    return `
      :host {
        display: inline-block;
      }

      button {
        display: flex;
        align-items: center;
        background: var(--surface, #f0f0f0);
        border: 1px solid var(--card-border, #E2E4E8);
        border-radius: var(--radius-pill, 9999px);
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: Inter, system-ui, sans-serif;
        text-align: left;
        ${sizeStyles.button}
      }

      button:hover {
        border-color: var(--input-border-focus, #999999);
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      }

      button:focus {
        outline: var(--focus, 2px solid #1C1C1E);
        outline-offset: 2px;
      }

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: var(--muted, #555555);
        ${sizeStyles.icon}
      }

      .placeholder {
        flex: 1;
        color: var(--muted, #555555);
        ${sizeStyles.placeholder}
      }

      .shortcut {
        margin-left: auto;
        background: var(--bg, #fafafb);
        border: 1px solid var(--kbd-border, #E2E4E8);
        border-radius: var(--radius-xs, 4px);
        color: var(--kbd-color, #333333);
        ${sizeStyles.shortcut}
      }

      @media (prefers-color-scheme: dark) {
        button {
          background: var(--surface, #14161a);
          border-color: var(--card-border, #24262c);
        }

        button:hover {
          border-color: var(--input-border-focus, #444444);
        }

        .icon {
          color: var(--muted, #a3a3a3);
        }

        .placeholder {
          color: var(--muted, #a3a3a3);
        }

        .shortcut {
          background: var(--bg, #0b0b0c);
          border-color: var(--kbd-border, #3a3d45);
          color: var(--kbd-color, #c9c9c9);
        }
      }

      @media (max-width: 640px) {
        .shortcut {
          display: none;
        }
      }
    `;
  }

  // Precondition: None
  // Postcondition: Returns HTML template string
  private getTemplate(): string {
    const placeholder = this.getPlaceholder();
    const shortcut = this.getShortcut();

    return `
      <button type="button" aria-label="${placeholder}">
        <span class="icon" aria-hidden="true">${icon('search', 18)}</span>
        <span class="placeholder">${placeholder}</span>
        <span class="shortcut">${shortcut}</span>
      </button>
    `;
  }

  // Precondition: Shadow DOM rendered
  // Postcondition: Click handler dispatches ft-spotlight-open
  private setupEventListeners(): void {
    const button = this.shadowRoot?.querySelector("button");
    if (!button) return;

    button.addEventListener("click", () => {
      window.dispatchEvent(new CustomEvent("ft-spotlight-open"));
    });
  }
}


customElements.define("ft-search-button", FtSearchButton);
